import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { useChat } from '../chat-provider';
import type { ChatMessage } from '../types';
import type { CanvasArtifact } from './canvas-pane';
import { MessageRenderer } from './message-renderer';
import { ErrorBanner } from './error-banner';
import { ConfirmDialog } from './confirm-dialog';
import { SystemPromptDialog } from './system-prompt-dialog';
import { QueueDisplay } from './queue-display';
import { ChatInput } from './chat-input';
import { MessageSquare, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

function ThinkingIndicator() {
    return (
        <div className="flex items-start gap-3 px-4 py-3 md:px-12">
            <div
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-sm"
                style={{ background: 'linear-gradient(to bottom right, #3b82f6, #4f46e5)' }}
            >
                AI
            </div>
            <div className="rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-800">
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                        <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: '0ms' }} />
                        <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: '150ms' }} />
                        <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-slate-400">Thinking…</span>
                </div>
            </div>
        </div>
    );
}

interface SuggestionCardProps {
    text: string;
    onClick: () => void;
}

function SuggestionCard({ text, onClick }: SuggestionCardProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left text-xs text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:text-slate-100"
        >
            {text}
        </button>
    );
}

const DEFAULT_SUGGESTIONS = [
    'Explain how this system works',
    'What capabilities are available?',
    'Help me draft a message',
    'Summarize recent activity',
];

interface ChatWindowProps {
    onOpenCanvas?: (artifact: CanvasArtifact) => void;
}

export function ChatWindow({ onOpenCanvas }: ChatWindowProps) {
    const {
        messages,
        status,
        activeConversationId,
        branchMap,
        conversationSystemPrompt,
        messageSendStatuses,
        isOffline,
        sendMessage,
        editMessage,
        deleteMessage,
        retryMessage,
        retrySendMessage,
        forkConversation,
        switchBranch,
        stopStreaming,
        setConversationSystemPrompt,
        queuedMessages,
        rateLimitedUntil,
        removeFromQueue,
    } = useChat();

    const isStreaming = status === 'streaming' || status === 'submitted';
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const [atBottom, setAtBottom] = useState(true);

    const [deleteTarget, setDeleteTarget] = useState<{
        messageId: string;
        isUser: boolean;
    } | null>(null);
    const [deleteBelow, setDeleteBelow] = useState(false);
    const [showSystemPrompt, setShowSystemPrompt] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isStreaming) {
                stopStreaming();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isStreaming, stopStreaming]);

    const branchInfoByIndex = useMemo(() => {
        const info: Record<
            number,
            { forkParentId: string; currentBranch: number; totalBranches: number }
        > = {};
        for (let i = 0; i < messages.length; i++) {
            if (messages[i].role !== 'user') continue;
            const forkParentId = i > 0 ? messages[i - 1].id : '__root__';
            const group = branchMap[forkParentId];
            if (group && group.branches.length > 1) {
                info[i] = {
                    forkParentId,
                    currentBranch: group.activeBranch,
                    totalBranches: group.branches.length,
                };
            }
        }
        return info;
    }, [messages, branchMap]);

    const handleEdit = useCallback(
        (messageId: string, newText: string) => {
            editMessage(messageId, newText);
        },
        [editMessage],
    );

    const handleDeleteRequest = useCallback(
        (messageId: string) => {
            const msg = messages.find((m) => m.id === messageId);
            if (!msg) return;
            setDeleteTarget({ messageId, isUser: msg.role === 'user' });
            setDeleteBelow(msg.role === 'user');
        },
        [messages],
    );

    const handleRetry = useCallback(
        (messageId: string, options?: { capabilityRef?: string }) => {
            retryMessage(messageId, options);
        },
        [retryMessage],
    );

    const handleFork = useCallback(
        (messageId: string) => {
            forkConversation(messageId);
        },
        [forkConversation],
    );

    const handleCopyLink = useCallback(
        (messageId: string) => {
            if (!activeConversationId) return;
            const url = `${window.location.origin}/chat/${activeConversationId}#msg_${messageId}`;
            navigator.clipboard.writeText(url);
            toast.success('Link copied to clipboard');
        },
        [activeConversationId],
    );

    const handleDeleteConfirm = useCallback(() => {
        if (!deleteTarget) return;
        deleteMessage(deleteTarget.messageId, deleteBelow);
        setDeleteTarget(null);
    }, [deleteTarget, deleteBelow, deleteMessage]);

    const scrollToBottom = useCallback(() => {
        virtuosoRef.current?.scrollToIndex({
            index: messages.length - 1,
            behavior: 'smooth',
        });
    }, [messages.length]);

    const showThinking = isStreaming && messages.length > 0 &&
        messages[messages.length - 1]?.role === 'assistant' &&
        messages[messages.length - 1]?.parts.length === 0;

    return (
        <div className="flex h-full flex-col">
            {isOffline && (
                <ErrorBanner
                    message="You are offline. Check your internet connection."
                    severity="warning"
                />
            )}

            {messages.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center px-6 py-12">
                    <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50 shadow-inner dark:bg-blue-950">
                        <MessageSquare className="h-8 w-8 text-blue-500" />
                    </div>
                    <h3 className="mb-2 text-lg font-semibold text-slate-800 dark:text-slate-200">
                        How can I help you?
                    </h3>
                    <p className="mb-8 max-w-md text-center text-sm text-slate-500">
                        Select an agent capability and ask anything. I can help with analysis, writing, coding, and more.
                    </p>
                    <div className="grid w-full max-w-lg gap-2 sm:grid-cols-2">
                        {DEFAULT_SUGGESTIONS.map((text) => (
                            <SuggestionCard
                                key={text}
                                text={text}
                                onClick={() => sendMessage(text)}
                            />
                        ))}
                    </div>
                </div>
            ) : (
                <div className="relative flex-1 overflow-hidden">
                    <Virtuoso
                        ref={virtuosoRef}
                        data={messages}
                        followOutput="smooth"
                        initialTopMostItemIndex={messages.length > 0 ? messages.length - 1 : 0}
                        atBottomStateChange={setAtBottom}
                        className="h-full dark:bg-[var(--chat-bg)]"
                        itemContent={(index: number, message: ChatMessage) => (
                            <div className="px-4 py-2 md:px-12">
                                <MessageRenderer
                                    message={message}
                                    isStreaming={
                                        isStreaming &&
                                        message.id === messages[messages.length - 1]?.id &&
                                        message.role === 'assistant'
                                    }
                                    isLastMessage={index === messages.length - 1}
                                    branchInfo={branchInfoByIndex[index]}
                                    sendStatus={messageSendStatuses[message.id]}
                                    onEdit={handleEdit}
                                    onDelete={handleDeleteRequest}
                                    onSwitchBranch={switchBranch}
                                    onRetry={handleRetry}
                                    onRetrySend={retrySendMessage}
                                    onFork={activeConversationId ? handleFork : undefined}
                                    onCopyLink={activeConversationId ? handleCopyLink : undefined}
                                    onSendMessage={sendMessage}
                                />
                            </div>
                        )}
                        components={{
                            Footer: showThinking ? ThinkingIndicator : undefined,
                        }}
                    />

                    {!atBottom && (
                        <button
                            onClick={scrollToBottom}
                            className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-slate-800 px-3 py-1.5 text-xs text-white shadow-lg transition-opacity hover:bg-slate-700"
                        >
                            <ArrowDown className="mr-1 inline h-3 w-3" />
                            Scroll to bottom
                        </button>
                    )}
                </div>
            )}

            <QueueDisplay
                queuedMessages={queuedMessages}
                rateLimitedUntil={rateLimitedUntil ?? undefined}
                onRemove={removeFromQueue}
            />

            <ChatInput />

            <ConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(open) => {
                    if (!open) setDeleteTarget(null);
                }}
                title="Delete message"
                description={
                    deleteTarget?.isUser
                        ? 'Delete this message and all responses below it? This cannot be undone.'
                        : 'Delete this message? This cannot be undone.'
                }
                confirmLabel="Delete"
                variant="destructive"
                onConfirm={handleDeleteConfirm}
            />

            {activeConversationId && (
                <SystemPromptDialog
                    open={showSystemPrompt}
                    onOpenChange={setShowSystemPrompt}
                    conversationId={activeConversationId}
                    currentPrompt={conversationSystemPrompt}
                    onSave={setConversationSystemPrompt}
                />
            )}
        </div>
    );
}
